import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

export function Dropdown({ trigger, children, align = 'left', direction = 'down', className }) {
  const [open, setOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState({})
  const triggerRef = useRef()
  const menuRef = useRef()

  function openMenu() {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) { setOpen(true); return }
    const style = { position: 'fixed', zIndex: 9999 }
    if (align === 'right') {
      style.right = window.innerWidth - rect.right
    } else {
      style.left = rect.left
    }
    if (direction === 'up') {
      style.bottom = window.innerHeight - rect.top + 4
    } else {
      style.top = rect.bottom + 4
    }
    setMenuStyle(style)
    setOpen(true)
  }

  // Clamp to viewport after first render
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return
    const menuRect = menuRef.current.getBoundingClientRect()
    setMenuStyle((prev) => {
      const next = { ...prev }
      if (menuRect.right > window.innerWidth - 8) {
        delete next.left
        next.right = 8
      }
      if (menuRect.bottom > window.innerHeight - 8) {
        const trigRect = triggerRef.current?.getBoundingClientRect()
        if (trigRect) {
          delete next.top
          next.bottom = window.innerHeight - trigRect.top + 4
        }
      }
      return next
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (!menuRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        setOpen(false)
      }
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open])

  const transformOrigin = direction === 'up'
    ? (align === 'right' ? 'bottom right' : 'bottom left')
    : (align === 'right' ? 'top right' : 'top left')

  return (
    <div ref={triggerRef} className="relative inline-block">
      <div onClick={() => open ? setOpen(false) : openMenu()}>{trigger}</div>
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, scale: 0.95, y: direction === 'up' ? 4 : -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: direction === 'up' ? 4 : -4 }}
              transition={{ duration: 0.12, ease: [0.23, 1, 0.32, 1] }}
              style={{ transformOrigin, ...menuStyle }}
              className={cn(
                'min-w-[160px] bg-black/70 backdrop-blur-xl border border-white/[0.08] rounded-xl shadow-xl shadow-black/40 py-1 overflow-y-auto max-h-[60vh]',
                className
              )}
              onClick={() => setOpen(false)}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  )
}

export function DropdownItem({ icon: Icon, label, onClick, danger, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 h-8 text-sm transition-colors text-left',
        danger ? 'text-red-400 hover:bg-red-500/10' : 'text-text-muted hover:text-text hover:bg-white/[0.06]',
        disabled && 'opacity-40 pointer-events-none'
      )}
    >
      {Icon && <Icon size={13} className="flex-shrink-0" />}
      {label}
    </button>
  )
}

export function DropdownSeparator() {
  return <div className="my-1 h-px bg-border mx-2" />
}
